import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Table } from '../../components/idsk';
import { formatDateTime } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { Loading } from '../common/LoadingPage';

export interface FieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface Revision {
  revision: number;
  timestamp: string;
  username: string | null;
  requestId: string | null;
  type: 'ADD' | 'MOD' | 'DEL';
  entityType: string;
  entityId: string;
  label: string;
  changes: FieldChange[];
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Field-level change list (before → after) with screen-reader prefixes instead of relying on strike-through. */
export function ChangeList({ changes, entityType }: { changes: FieldChange[]; entityType: string }) {
  const { t } = useTranslation();
  if (changes.length === 0) return <>{t('history.noFieldChanges')}</>;
  return (
    <ul className="govuk-list">
      {changes.map((c) => {
        const before = formatValue(c.before);
        const after = formatValue(c.after);
        return (
          <li key={c.field}>
            <strong>{t(`history.fields.${entityType}.${c.field}`, { defaultValue: c.field })}</strong>:{' '}
            <span className="app-diff-before">
              <span className="govuk-visually-hidden">{t('history.before')} </span>
              {before || t('history.empty')}
            </span>{' '}
            <span aria-hidden="true">→</span>{' '}
            <span>
              <span className="govuk-visually-hidden">{t('history.after')} </span>
              {after || t('history.empty')}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** "História zmien" tab (ADMIN): Envers revisions of an entity. */
export function EntityHistory({ entity, id }: { entity: 'subjects' | 'inspectors' | 'inspections'; id: string }) {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: [entity, id, 'history'], queryFn: () => api<Revision[]>(`/${entity}/${id}/history`) });
  if (query.error) return <QueryError error={query.error} />;
  if (query.isPending) return <Loading />;
  return (
    <Table<Revision>
      caption={t('history.caption')}
      rows={query.data}
      rowKey={(r) => `${r.revision}-${r.entityType}-${r.entityId}`}
      columns={[
        { key: 'when', header: t('history.when'), render: (r) => formatDateTime(r.timestamp) },
        { key: 'who', header: t('history.who'), render: (r) => r.username ?? '' },
        {
          key: 'what',
          header: t('history.what'),
          render: (r) => (
            <>
              {t(`history.type.${r.type}`)}
              {r.entityType !== 'SUBJECT' && r.entityType !== 'INSPECTOR' && r.entityType !== 'INSPECTION' && (
                <>
                  {' – '}
                  {t(`history.entity.${r.entityType}`, { defaultValue: r.entityType })}
                  {r.label ? `: ${r.label}` : ''}
                </>
              )}
            </>
          ),
        },
        { key: 'changes', header: t('history.changes'), render: (r) => <ChangeList changes={r.changes} entityType={r.entityType} /> },
      ]}
      emptyText={t('history.empty')}
    />
  );
}
